import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const useTPRMDialog = ({
  onClose,
  closeOnEscape = true,
  enabled = true,
  returnFocusRef,
}) => {
  const dialogRef = useRef(null);
  const optionsRef = useRef({ onClose, closeOnEscape, enabled });
  optionsRef.current = { onClose, closeOnEscape, enabled };

  useEffect(() => {
    if (!enabled) return undefined;
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const previouslyFocused =
      returnFocusRef?.current || document.activeElement;
    const previousAriaLabel = previouslyFocused?.getAttribute?.("aria-label");
    const previousText = previouslyFocused?.textContent?.trim();
    const previousTagName = previouslyFocused?.tagName;
    const getFocusableElements = () =>
      Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) =>
          !element.matches(":disabled") &&
          element.getAttribute("aria-hidden") !== "true",
      );
    const focusableElements = getFocusableElements();
    (focusableElements[0] || dialog).focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && optionsRef.current.closeOnEscape) {
        event.preventDefault();
        optionsRef.current.onClose?.();
        return;
      }
      if (event.key !== "Tab") return;

      const currentFocusable = getFocusableElements();
      if (!currentFocusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = currentFocusable[0];
      const last = currentFocusable[currentFocusable.length - 1];
      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.setTimeout(() => {
        const replacement = previousAriaLabel
          ? Array.from(document.querySelectorAll("[aria-label]")).find(
              (element) =>
                element.getAttribute("aria-label") === previousAriaLabel,
            )
          : null;
        if (replacement && replacement !== previouslyFocused) {
          replacement.focus();
        } else if (previouslyFocused?.isConnected) {
          previouslyFocused.focus();
        } else if (replacement) {
          replacement.focus();
        } else if (previousText && previousTagName) {
          Array.from(document.querySelectorAll(previousTagName))
            .find((element) => element.textContent.trim() === previousText)
            ?.focus();
        }
      }, 0);
    };
  }, [enabled]);

  return dialogRef;
};

export default useTPRMDialog;
